import { ColorPicker, Form, Input, Select, Slider } from 'antd';
import { collect } from 'collect.js';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import FontPicker from 'react-fontpicker-ts';
import ImagePicker from '../../../../form/general/ImagePicker';
import { PolygraphyProductImageType, PolygraphyProductUrlType } from '../../../../../services/exports/Constants';

export default function StickerOneForm({
  data,
  images,
  polygraphyProductUrlTypeOptions,
  onChange,
  onImageChange,
  renderFeedbackLabel,
  className,
  onSave,
  setData,
}) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'Components:Marketing:Polygraphy:Products:Templates:Sticker:StickerOneForm',
  });

  const versions = useVersions();

  const onVersionChange = (option) => {
    setData(option.data);
    onSave(option.data);
  };

  return (
    <Form layout="vertical" className={className}>
      {renderFeedbackLabel}
      <Select
        placeholder={t('form.versions.placeholder')}
        options={versions}
        defaultValue={versions[0]}
        onChange={(_, option) => onVersionChange(option)}
        className="mb-md"
      />
      <Form.Item label={t('form.font_family.label')}>
        <FontPicker
          defaultValue={data.font_family}
          value={(value) => onChange('font_family', value)}
          fontVariants={(variants) => onChange('font_variants', variants.variants)}
        />
      </Form.Item>
      <Form.Item label={t('form.background_color.label')}>
        <ColorPicker
          showText
          value={data.background_color}
          onChange={(color) => onChange('background_color', color.toHexString())}
        />
      </Form.Item>
      <Form.Item label={t('form.promo_frame_primary_color.label')}>
        <ColorPicker
          showText
          value={data.promo_frame_primary_color}
          onChange={(color) => onChange('promo_frame_primary_color', color.toHexString())}
        />
      </Form.Item>
      <Form.Item label={t('form.promo_frame_discount_label_color.label')}>
        <ColorPicker
          showText
          value={data.promo_frame_discount_label_color}
          onChange={(color) => onChange('promo_frame_discount_label_color', color.toHexString())}
        />
      </Form.Item>
      <Form.Item label={t('form.vip_members_cta_color.label')}>
        <ColorPicker
          showText
          value={data.vip_members_cta_color}
          onChange={(color) => onChange('vip_members_cta_color', color.toHexString())}
        />
      </Form.Item>
      <Form.Item label={t('form.url_cta_color.label')}>
        <ColorPicker
          showText
          value={data.url_cta_color}
          onChange={(color) => onChange('url_cta_color', color.toHexString())}
        />
      </Form.Item>
      <Form.Item label={t('form.url_color.label')}>
        <ColorPicker showText value={data.url_color} onChange={(color) => onChange('url_color', color.toHexString())} />
      </Form.Item>
      <Form.Item label={t('form.url_type.label')}>
        <Select
          value={data.url_type}
          options={polygraphyProductUrlTypeOptions}
          className="w-full"
          onChange={(value) => onChange('url_type', value)}
          placeholder={t('form.url_type.placeholder')}
        />
        {data?.url_type === PolygraphyProductUrlType.Custom && (
          <Input
            className="mt-xs"
            placeholder={t('form.url.placeholder')}
            value={data.url}
            defaultValue={data.url}
            onChange={(e) => onChange('url', e.target.value)}
          />
        )}
      </Form.Item>
      <Form.Item label={t('form.qr_code_border_color.label')}>
        <ColorPicker
          showText
          value={data.qr_code_border_color}
          onChange={(color) => onChange('qr_code_border_color', color.toHexString())}
        />
      </Form.Item>
      <Form.Item label={t('form.footer_background_color.label')}>
        <ColorPicker
          showText
          value={data.footer_background_color}
          onChange={(color) => onChange('footer_background_color', color.toHexString())}
        />
      </Form.Item>
      <Form.Item label={t('form.logo.label')}>
        <ImagePicker
          images={[collect(images).firstWhere('type', PolygraphyProductImageType.Logo)?.file_url]}
          onChange={(value) => onImageChange(PolygraphyProductImageType.Logo, collect(value).first())}
          format="image/png,image/jpeg"
          withCropper
          cropperProps={{
            maxAspect: 10,
          }}
        />
      </Form.Item>
      <Form.Item label={t('form.logo_zoom.label')}>
        <Slider
          min={0}
          max={10}
          step={1}
          marks={{ 0: '0', 10: '10' }}
          value={data.logo_zoom ?? 0}
          onChange={(value) => onChange('logo_zoom', value)}
        />
      </Form.Item>
    </Form>
  );
}

const useVersions = () => {
  const { franchise } = useSelector((store) => store.currentCompany);
  const primaryColor = franchise?.primary_color ?? '#69CE8C';

  return collect([
    {
      label: '(Light Mode)',
      data: {
        background_color: '#FFFFFF',
        promo_frame_primary_color: primaryColor,
        promo_frame_discount_label_color: primaryColor,
        vip_members_cta_color: '#000000',
        url_cta_color: '#000000',
        url_color: primaryColor,
        qr_code_border_color: primaryColor,
      },
    },
    {
      label: '(Dark Mode)',
      data: {
        background_color: '#231F20',
        promo_frame_primary_color: primaryColor,
        promo_frame_discount_label_color: primaryColor,
        vip_members_cta_color: '#FFFFFF',
        url_cta_color: '#FFFFFF',
        url_color: primaryColor,
        qr_code_border_color: primaryColor,
      },
    },
    {
      label: '(Light Mode)',
      data: {
        background_color: '#FFFFFF',
        promo_frame_primary_color: primaryColor,
        promo_frame_discount_label_color: primaryColor,
        vip_members_cta_color: '#FFFFFF',
        url_cta_color: '#FFFFFF',
        url_color: '#FFFFFF',
        qr_code_border_color: primaryColor,
        footer_background_color: primaryColor,
      },
    },
  ])
    .map((item, index) => ({
      ...item,
      label: `${index + 1} ${item.label}`,
      value: index,
    }))
    .toArray();
};
